import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef, inject, input, viewChild, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { StreamTypes } from "@auvious/rtc";
import {
  ConferenceService,
  debug,
  debugError,
  GenericErrorHandler,
} from "../../services";

@Component({
  selector: "app-stream-view",
  templateUrl: "./stream-view.component.html",
  styleUrls: ["./stream-view.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
/**
 * uses an object-fit polyfill
 * https://github.com/constancecchen/object-fit-polyfill
 */
export class StreamViewComponent implements OnInit, AfterViewInit, OnDestroy {
  private errorHandler = inject(GenericErrorHandler);
  private conferenceService = inject(ConferenceService);
  private destroyRef = inject(DestroyRef);

  readonly type = input<StreamTypes>(undefined);
  readonly muted = input<boolean>(undefined);
  readonly id = input<string>(undefined);

  readonly mediaElementReady = output<HTMLAudioElement | HTMLVideoElement>();
  readonly mediaElementDestroyed = output<{
    element: HTMLAudioElement | HTMLVideoElement;
    id: string;
}>();
  readonly mediaElementPlayError = output<string>();
  readonly autoplayBlocked = output<string>();

  readonly container = viewChild("streamsContainer", { read: ViewContainerRef });
  readonly audioTemplate = viewChild<TemplateRef<any>>("audioTagTpl");
  readonly videoTemplate = viewChild<TemplateRef<any>>("videoTagTpl");

  public mediaElement: HTMLVideoElement | HTMLAudioElement;

  private _stream: MediaStream;

  @Input()
  set stream(value: MediaStream) {
    this._stream = value;
    debug("stream-view new mediastream", this.stream);

    if (this.mediaElement && this.mediaElement.srcObject !== this.stream) {
      this.mediaElement.srcObject = this.stream;
    }
  }

  get stream() {
    return this._stream;
  }

  constructor() {
    this.conferenceService.playPausedStreams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.play();
    });
  }

  private async play() {
    if (!this.isPaused) {
      return true;
    }

    const id = this.id();
    debug("playing element :" + id);

    try {
      await this.mediaElement?.play();
      return true;
    } catch (ex) {
      debugError("play() error", ex);

      if (/^NotAllowedError/.test(ex)) {
        this.autoplayBlocked.emit(id);
      } else {
        this.errorHandler.handleError(ex);
      }

      return false;
    }
  }

  ngOnDestroy() {
    this.mediaElementDestroyed.emit({
      element: this.mediaElement,
      id: this.id(),
    });
  }

  ngOnInit() {}

  // creates audio and video if local and is video type or one for remote
  private createElements(): HTMLAudioElement | HTMLVideoElement | null {
    let element: HTMLAudioElement | HTMLVideoElement;

    if (this.type() === StreamTypes.MIC) {
      element = this.container()
        .createEmbeddedView(this.audioTemplate())
        .rootNodes.find((el) => el.nodeName === "AUDIO");
    } /* if (
      this.type === StreamTypes.CAM ||
      this.type === StreamTypes.VIDEO ||
      this.type === StreamTypes.SCREEN
    ) */ else {
      element = this.container()
        .createEmbeddedView(this.videoTemplate())
        .rootNodes.find((el) => el.nodeName === "VIDEO");
    }

    return element;
  }

  ngAfterViewInit() {
    try {
      this.mediaElement = this.createElements();

      this.mediaElement.addEventListener(
        "loadedmetadata",
        () => {
          setTimeout(() => this.play(), 50);
        },
        {
          once: true,
        }
      );

      this.mediaElement.addEventListener("pause", () => {
        setTimeout(() => this.play(), 100);
      });

      if (this.stream /* !== this.conferenceService.localMediaStream */) {
        this.mediaElement.srcObject = this.stream;
      }

      this.mediaElementReady.emit(this.mediaElement);
    } catch (ex) {
      debugError(ex);
    }
  }

  private checkPlayback() {
    return new Promise<boolean>((resolve) => {
      const currentTimeA = this.mediaElement?.currentTime;
      const qualA = (
        this.mediaElement as HTMLVideoElement
      )?.getVideoPlaybackQuality?.();

      setTimeout(() => {
        const currentTimeB = this.mediaElement?.currentTime;
        const qualB = (
          this.mediaElement as HTMLVideoElement
        )?.getVideoPlaybackQuality?.();

        resolve(
          currentTimeB !== currentTimeA &&
            (!qualA || qualB.totalVideoFrames - qualA.totalVideoFrames > 0)
        );
      }, 600);
    });
  }

  get isPaused() {
    return this.mediaElement?.paused /* &&
      this.mediaElement.readyState < this.mediaElement.HAVE_FUTURE_DATA */;
  }
}
